export type Subcontroller = string;

export type MVStatus = 
  | 'Not Started'
  | 'Active'
  | 'Waiting for Steady State'
  | 'Step Complete'
  | 'Complete'
  | 'Rejected'
  | 'Model Ready'
  | 'Excluded';

export type ReadinessScore = 'Insufficient' | 'Adequate' | 'Ready for Modeling';

export type UserRole = 'APC Engineer' | 'Process Engineer' | 'Administrator';

export type StepDirection = '+' | '-';

export type YesNo = 'Yes' | 'No';

export type StepTestStatus = 'IN_PROGRESS' | 'PASS' | 'FAIL';
export type PassFail = StepTestStatus;

export type DisturbanceCategory = 
  | 'Pump swaps'
  | 'Analyzer maintenance'
  | 'Compressor changes'
  | 'Feed changes'
  | 'Utility problems'
  | 'Product routing changes'
  | 'Weather impacts'
  | 'Treaters';

export interface Project {
  id: string;
  name: string;
  description: string;
  facility: string;
  createdAt: string;
  status: 'Active' | 'Archived' | 'Planned';
  targetMovesPerMv?: number; // Target moves per variable (default: 20)
  defaultHoldDuration?: number; // Default settling hold duration in minutes (default: 60)
}

// Backward-compatibility alias
export type Campaign = Project;

export interface ManipulatedVariable {
  id: string;
  campaignId: string;
  tag: string;
  description: string;
  controller: Subcontroller;
  processArea?: string;
  units: string;
  recommendedStepSize: number;
  approvedStepSize: number;
  status: MVStatus;
  positiveMoves: number;
  negativeMoves: number;
  validMoves: number;
  rejectedMoves: number;
  targetMoves?: number; // Optional custom target override for this variable
  notes: string;
  updatedAt: string;
}

export interface SteadyStateChecklist {
  heldRequiredDuration: boolean;
  noOperatorIntervention: boolean;
  noEquipmentChanges: boolean;
  noMajorFeedChanges: boolean;
  analyzerResponseObserved: boolean;
  stableResponseObserved: boolean;
}

export interface StepTest {
  id: string;
  campaignId: string;
  mvId: string;
  mvTag: string;
  timestamp: string; // ISO string or YYYY-MM-DD HH:mm
  engineer: string;
  operator: string;
  direction: StepDirection;
  stepSize: number;
  baselineSp: number;
  targetSp: number;
  holdDuration: number; // in minutes
  completedAt?: string | null;
  steadyStateAchieved?: YesNo;
  disturbancesPresent?: YesNo;
  analyzerResponseObserved?: YesNo;
  dataQualityRating?: number; // 1 to 5
  checklist?: SteadyStateChecklist;
  status: StepTestStatus;
  comments: string;
  createdAt: string;
}

export const getStepStartTimeMs = (step: StepTest): number => {
  if (step.createdAt) {
    const ms = new Date(step.createdAt).getTime();
    if (!isNaN(ms)) return ms;
  }
  if (step.timestamp) {
    const ms = new Date(step.timestamp.replace(' ', 'T')).getTime();
    if (!isNaN(ms)) return ms;
  }
  return Date.now();
};

export interface Disturbance {
  id: string;
  campaignId: string;
  timestamp: string;
  category: DisturbanceCategory;
  controller?: Subcontroller | 'Plant-Wide';
  description: string;
  engineer: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CorrelationRule {
  id: string;
  name: string;
  tagsA: string[];
  tagsB: string[];
  description: string;
  cautionLevel: 'warning' | 'alert';
}

export interface ProjectSummaryStats {
  projectName: string;
  projectProgressPct: number;
  campaignName: string; // compatibility alias
  campaignProgressPct: number; // compatibility alias
  totalMVs: number;
  totalValidMoves: number;
  targetMovesTotal: number;
  remainingMoves: number;
  overallModelReadinessPct: number;
  activeTestsCount: number;
  rejectedTestsCount: number;
  controllerStats: Record<Subcontroller, {
    totalMvs: number;
    validMoves: number;
    targetMoves: number;
    readinessPct: number;
    mvsReadyCount: number;
    mvsAdequateCount: number;
    mvsInsufficientCount: number;
  }>;
}

export type CampaignSummaryStats = ProjectSummaryStats;
